/**
 * Weekly Insight Notifications Service
 *
 * Insight nudges are delivered weekly at a per-user "anchor" — a local weekday + hour.
 * There are two ways the anchor gets set:
 *
 *   • explicit — the user taps "Notify me" on an insight completion card. We anchor to
 *     that exact local moment (they're actively engaging with an insight).
 *   • default  — the user enabled push through some *other* channel (Profile toggle,
 *     No-Spend Weekend, Cart Companion) and we never captured an insight-specific time.
 *     `ensureDefaultInsightAnchor` backfills a sensible default so insight checks still
 *     run. Called from the single grant chokepoint (NotificationContext.fetchAndSaveToken),
 *     so every opt-in path — and existing granted users on next app open — is covered.
 *
 * Delivery, cadence, quiet hours and the off-switch all live server-side (see
 * functions-enough/content/insight_notifications.py). This only records intent + timing.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

export type WeeklyInsightNotifySource = 'explicit' | 'default';

export interface WeeklyInsightNotify {
  /** Weekly insight checks run for this user. The server additionally gates on the
   *  "insights" notification category, which is the Profile off-switch. */
  active: boolean;
  /** Local weekday at opt-in, 0=Monday … 6=Sunday (matches Python datetime.weekday()). */
  anchorWeekday: number;
  /** Local hour at opt-in, 0–23. */
  anchorHour: number;
  /** IANA timezone the anchor was captured in (e.g. "America/New_York"). */
  timezone: string;
  /** How the anchor was set — an explicit anchor is never overwritten by a default. */
  source: WeeklyInsightNotifySource;
}

/** Friendly daytime window for defaults — keeps auto-set anchors out of odd hours. */
const DEFAULT_HOUR = 10;
const FRIENDLY_START = 8;
const FRIENDLY_END = 20;

/** Convert JS getDay() (0=Sun…6=Sat) to Python weekday() (0=Mon…6=Sun). */
function toPythonWeekday(jsDay: number): number {
  return (jsDay + 6) % 7;
}

/** Clamp odd-hour opt-ins (late night / pre-dawn) to a friendly default. */
function friendlyHour(hour: number): number {
  return hour >= FRIENDLY_START && hour <= FRIENDLY_END ? hour : DEFAULT_HOUR;
}

function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
}

async function writeAnchor(
  uid: string,
  anchor: { anchorWeekday: number; anchorHour: number; source: WeeklyInsightNotifySource },
): Promise<void> {
  await setDoc(
    doc(db, 'userContent', uid),
    {
      weeklyInsightNotify: {
        active: true,
        timezone: localTimezone(),
        enabledAt: serverTimestamp(),
        ...anchor,
      },
    },
    { merge: true },
  );
}

/**
 * Explicit opt-in: anchor weekly insight nudges to the current local weekday + hour.
 * Always wins over any previously-set default. Idempotent.
 */
export async function enableWeeklyInsightNotifications(uid: string): Promise<void> {
  if (!uid) return;
  const now = new Date();
  await writeAnchor(uid, {
    anchorWeekday: toPythonWeekday(now.getDay()),
    anchorHour: now.getHours(),
    source: 'explicit',
  });
}

/**
 * Default opt-in: set a sensible anchor for a push-enabled user who never set one
 * explicitly. No-op if any anchor already exists, so it never clobbers an explicit
 * anchor (or an earlier default).
 */
export async function ensureDefaultInsightAnchor(uid: string): Promise<void> {
  if (!uid) return;
  const snap = await getDoc(doc(db, 'userContent', uid));
  const existing = snap.data()?.weeklyInsightNotify as Partial<WeeklyInsightNotify> | undefined;
  if (existing && typeof existing.anchorHour === 'number') return;
  const now = new Date();
  await writeAnchor(uid, {
    anchorWeekday: toPythonWeekday(now.getDay()),
    anchorHour: friendlyHour(now.getHours()),
    source: 'default',
  });
}
